/**
 * @fileoverview Used In Panel
 * 
 * Shows all diagrams that link to the current diagram.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import React from 'react';
import { useReferencingDiagrams } from '../../hooks/useDiagramLinkHooks';
import { useNavigationStore } from '../../stores/navigationStore';
import { useDiagramInfo } from '../../hooks/useDiagramInfo';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import { useLinkPreviewStore } from '../../stores/linkPreviewStore';
import { Button } from '../ui/button';
import { Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import * as Y from 'yjs';
import { base64ToUint8Array } from '@/utils/yjs';
import { useTranslation } from '@/hooks/useTranslation';
import { readYjsValue } from '../../lib/diagram/yjsCompat';

interface UsedInPanelProps {
  diagramId: string;
  className?: string;
}

export const UsedInPanel: React.FC<UsedInPanelProps> = ({
  diagramId,
  className,
}) => {
  const { t } = useTranslation('diagram');
  const { data: links, isLoading, error } = useReferencingDiagrams(diagramId);
  const { breadcrumbStack, isNavigating } = useNavigationStore();

  // Multiple shapes in the same diagram can link to the current diagram, so the API returns multiple link records.
  // For the panel UX, group by source diagram and show a single row per diagram.
  // Must be defined unconditionally (Rules of Hooks).
  const grouped = React.useMemo(() => {
    if (!links || links.length === 0) return [];
    const map = new Map<string, { sourceDiagramId: string; linkIds: string[]; sourceShapeIds: string[] }>();
    for (const link of links) {
      const sourceId = link.sourceDiagramId;
      if (!sourceId) continue;
      const entry = map.get(sourceId) || { sourceDiagramId: sourceId, linkIds: [], sourceShapeIds: [] };
      if (link.id && !entry.linkIds.includes(link.id)) entry.linkIds.push(link.id);
      if (link.sourceShapeId && !entry.sourceShapeIds.includes(link.sourceShapeId)) entry.sourceShapeIds.push(link.sourceShapeId);
      map.set(sourceId, entry);
    }
    return Array.from(map.values());
  }, [links]);

  const handleNavigate = (sourceDiagramId: string, folderId: string, sourceShapeId?: string | null) => {
    if (isNavigating || !folderId) return;

    // Best-effort: if the target diagram is already in the breadcrumb stack, preserve its ancestor chain.
    const idx = breadcrumbStack.findIndex((it) => it.diagramId === sourceDiagramId);
    const nextStack = idx >= 0 ? breadcrumbStack.slice(0, idx) : [];
    try {
      sessionStorage.setItem('diagram-nav-stack', JSON.stringify({ targetDiagramId: sourceDiagramId, breadcrumbStack: nextStack }));
      sessionStorage.removeItem('diagram-nav-last'); // legacy
    } catch {}

    const shapeParam = sourceShapeId ? `?shape=${encodeURIComponent(sourceShapeId)}` : '';
    window.location.href = `/f/${folderId}/d/${sourceDiagramId}${shapeParam}`;
  };

  if (isLoading) {
    return (
      <div className={cn('px-4 py-3 flex items-center justify-center', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          {t('linksPanel.usedIn.loadingReferences', { defaultValue: 'Loading references...' })}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('px-4 py-3', className)}>
        <div className="text-sm text-destructive">
          {t('linksPanel.usedIn.loadFailed', { defaultValue: 'Failed to load references' })}
        </div>
      </div>
    );
  }

  if (!links || links.length === 0) {
    return (
      <div className={cn('px-4 py-3', className)}>
        <div className="text-sm text-muted-foreground">
          {t('linksPanel.usedIn.notReferenced', {
            defaultValue: 'This diagram is not referenced by any other diagrams.',
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('px-4 py-3 space-y-3', className)}>
      <div className="text-sm font-medium">
        {t('linksPanel.usedIn.title', { count: grouped.length, defaultValue: 'Used In ({{count}})' })}
      </div>
      
      <div className="space-y-2">
        {grouped.map((g) => (
          <UsedInDiagramItem
            key={g.sourceDiagramId}
            targetDiagramId={diagramId}
            sourceDiagramId={g.sourceDiagramId}
            sourceShapeIds={g.sourceShapeIds}
            shapeCount={g.sourceShapeIds.length}
            onNavigate={handleNavigate}
            isNavigating={isNavigating}
          />
        ))}
      </div>
    </div>
  );
};

interface UsedInDiagramItemProps {
  targetDiagramId: string;
  sourceDiagramId: string;
  sourceShapeIds: string[];
  shapeCount: number;
  onNavigate: (diagramId: string, folderId: string, sourceShapeId?: string | null) => void;
  isNavigating: boolean;
}

const UsedInDiagramItem: React.FC<UsedInDiagramItemProps> = ({ targetDiagramId, sourceDiagramId, sourceShapeIds, shapeCount, onNavigate, isNavigating }) => {
  const { t } = useTranslation('diagram');
  const { data: diagramInfo, isLoading } = useDiagramInfo(sourceDiagramId);
  const authenticatedFetch = useAuthenticatedFetch();
  const openPreview = useLinkPreviewStore((s) => s.open);

  // Source diagram can accumulate stale link rows if shapes were deleted historically.
  // When the backend returns multiple (or mismatched) sourceShapeIds, validate against the
  // current snapshot to compute a true "linked shapes" count for this target diagram.
  const { data: snapshotLinkedShapeIds, isFetching: isFetchingSnapshot, isSuccess: snapshotOk } = useQuery<string[]>({
    queryKey: ['diagram', sourceDiagramId, 'snapshot-linked-shapes', targetDiagramId],
    enabled: Boolean(sourceDiagramId) && Boolean(targetDiagramId) && shapeCount > 1,
    queryFn: async () => {
      const urls = [
        `/api/diagrams/${encodeURIComponent(sourceDiagramId)}/snapshot`,
        `/api/link/diagrams/${encodeURIComponent(sourceDiagramId)}/snapshot`,
      ];
      let resp: Response | null = null;
      for (const url of urls) {
        // Include credentials to support link-based access cookies in addition to JWT.
        const r = await authenticatedFetch(url, { credentials: 'include' });
        if (r.ok) { resp = r; break; }
      }
      if (!resp) return [];
      const j = await resp.json();
      const b64 = j?.snapshotBytesBase64 as string | undefined;
      if (!b64) return [];

      const bytes = base64ToUint8Array(b64);
      const doc = new Y.Doc();
      Y.applyUpdate(doc, bytes, 'server');

      const shapes = doc.getMap('shapes');
      const ids: string[] = [];
      shapes.forEach((raw: any, id: any) => {
        const shape = readYjsValue<Record<string, any>>(raw);
        const shapeId = String(id);
        const attrs = shape?.attrs || {};
        if (attrs?.linkedDiagramId === targetDiagramId && typeof attrs?.linkId === 'string' && attrs.linkId.length > 0) {
          ids.push(shapeId);
        }
      });
      return ids;
    },
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-2 border rounded-md">
        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          {t('linksPanel.usedIn.itemLoading', { defaultValue: 'Loading...' })}
        </span>
      </div>
    );
  }

  if (!diagramInfo) {
    return null;
  }

  const effectiveShapeIds = (snapshotOk && snapshotLinkedShapeIds) ? snapshotLinkedShapeIds : sourceShapeIds;
  const effectiveCount = effectiveShapeIds.length > 0 ? effectiveShapeIds.length : shapeCount;

  return (
    <div
      className="flex items-center justify-between p-2 border rounded-md hover:bg-muted/50 transition-colors cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(sourceDiagramId, diagramInfo.folderId, effectiveCount === 1 ? (effectiveShapeIds[0] ?? null) : null)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onNavigate(sourceDiagramId, diagramInfo.folderId, effectiveCount === 1 ? (effectiveShapeIds[0] ?? null) : null);
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {diagramInfo.name ||
            (t('linksPanel.link.untitledDiagram', { defaultValue: 'Untitled Diagram' }) as string)}
        </div>
        <div className="text-xs text-muted-foreground">
          {isFetchingSnapshot ? (
            <>{t('linksPanel.usedIn.validatingLinks', { defaultValue: 'Validating links...' })}</>
          ) : effectiveCount > 1 ? (
            <>
              {t('linksPanel.usedIn.fromNShapes', {
                count: effectiveCount,
                defaultValue: 'From {{count}} shapes',
              })}
            </>
          ) : effectiveShapeIds[0] ? (
            <>
              {t('linksPanel.usedIn.fromShape', {
                id: effectiveShapeIds[0].slice(0, 8),
                defaultValue: 'From shape: {{id}}…',
              })}
            </>
          ) : (
            <>{t('linksPanel.usedIn.fromOneShape', { defaultValue: 'From 1 shape' })}</>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openPreview(sourceDiagramId);
        }}
        disabled={isNavigating || !diagramInfo.folderId}
        className="ml-2"
        aria-label={t('linksPanel.usedIn.previewAriaLabel', { defaultValue: 'Preview diagram' }) as string}
      >
        <ExternalLink className="h-3 w-3" />
      </Button>
    </div>
  );
};
